<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
    <div class="bg-white rounded-xl shadow-2xl w-[98vw] h-[95vh] flex flex-col overflow-hidden">
      <!-- Header -->
      <div class="bg-gray-50 border-b p-4 flex items-center justify-between">
        <div>
          <h2 class="text-xl font-semibold text-gray-800">Fixture Player Lines Timeline v3</h2>
          <div class="text-sm text-gray-600 mt-1">
            <span class="font-medium">Fixture ID:</span> {{ fixtureId }} 
          </div>
          <div class="text-xs text-gray-500 mt-1">
            <i class="fas fa-info-circle mr-1"></i>
            <span class="font-semibold">Chain:</span> Represents a bookmaker/chain identifier (user_id). 
            Entries are grouped by player → market → chain, then by changes in suspension status, odds, probabilities, and closed status.
          </div>
        </div>
        <button 
          @click="$emit('close')"
          class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Main Content Area -->
      <div class="flex-1 overflow-auto p-4 bg-gray-200">
        <!-- Horizontal scrollable container for the table -->
        <div class="overflow-x-auto">
          <!-- Loading State -->
          <div v-if="loading" class="flex items-center justify-center h-64">
            <div class="text-center">
              <i class="fas fa-spinner fa-spin text-2xl text-blue-500 mb-2"></i>
              <p class="text-gray-600">Loading data...</p>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="flex items-center justify-center h-64">
            <div class="text-center">
              <i class="fas fa-exclamation-triangle text-2xl text-red-500 mb-2"></i>
              <p class="text-red-600 mb-2">{{ error }}</p>
              <button @click="loadTimelineData" class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
                Try Again
              </button>
            </div>
          </div>

          <!-- Content -->
          <div v-else>
            <!-- Grid Header -->
            <div class="bg-gradient-to-r from-slate-800 to-slate-900 rounded-t-xl min-w-[2400px] shadow-lg">
              <div class="flex items-center p-4 text-white text-sm font-semibold">
                <div class="w-40 flex-shrink-0">Player</div>
                <div class="flex-1 grid grid-cols-12 gap-1 min-w-[2400px]">
                  <div class="text-center">Points</div>
                  <div class="text-center">Rebounds</div>
                  <div class="text-center">Assists</div>
                  <div class="text-center">3 Points</div>
                  <div class="text-center">Turnovers</div>
                  <div class="text-center">Blocks</div>
                  <div class="text-center">Steals</div>
                  <div class="text-center">PRA</div>
                  <div class="text-center">PR</div>
                  <div class="text-center">PA</div>
                  <div class="text-center">RA</div>
                  <div class="text-center">BS</div>
                </div>
              </div>
            </div>

            <!-- Grid Body -->
            <div class="bg-white border border-t-0 border-slate-200 rounded-b-xl min-w-[2400px] shadow-lg">
              <div v-if="processedData.length === 0" class="text-center py-12">
                <i class="fas fa-chart-line text-4xl text-gray-300 mb-4"></i>
                <p class="text-gray-500 text-lg">No timeline data found</p>
                <p class="text-gray-400 text-sm">Try adjusting your filters or check if data exists for this fixture</p>
              </div>
              
              <div v-else>
                <!-- Group data by player -->
                <div v-for="player in processedData" :key="player.player_id" 
                     class="border-b border-slate-100 hover:bg-slate-50 transition-all duration-200 group">
                  <div class="flex items-start p-4 bg-gray-100">
                    <!-- Player Info -->
                    <div class="w-40 flex-shrink-0 flex items-center gap-3">
                      <div class="flex-1">
                        <div class="font-semibold text-slate-800 text-sm">{{ player.player_name }}</div>
                        <div class="text-xs text-slate-500 font-medium">{{ player.player_team_name }}</div>
                        <div class="text-xs text-slate-400 mt-1">{{ player.chains_count }} chain(s)</div>
                      </div>
                    </div>
                    
                    <!-- Market Grid with Timeline -->
                    <div class="flex-1 grid grid-cols-12 gap-1 min-w-[2400px]">
                      <div v-for="market in ['points', 'total_rebounds', 'assists', 'three_point_field_goal', 'turnovers', 'blocks', 'steals', 'pra', 'pr', 'pa', 'ra', 'bs']" 
                           :key="market" class="relative market-cell">
                        <div class="border-[1px] border-slate-300 rounded-xl p-3 min-h-[200px] flex flex-col transition-all duration-300 hover:shadow-lg hover:border-blue-400 bg-white">
                          <!-- Market Header -->
                          <div class="text-sm font-bold text-slate-700 text-center mb-3 flex items-center justify-center gap-2 pb-2 border-b-2 border-slate-200">
                            <i class="fas fa-chart-line text-blue-500"></i>
                            <span>{{ getMarketDisplayName(market) }}</span>
                          </div>
                          
                          <!-- Market Timeline Data - Chain Groups -->
                          <div v-if="player.markets[market] && player.markets[market].chains" class="flex-1 space-y-2  ">
                            <div v-for="(chainGroup, chainId) in player.markets[market].chains" :key="chainId" 
                                 class="shadow-md hover:shadow-lg transition-shadow">
                              <!-- Chain Header with Tooltip -->
                              <div class="flex items-center justify-between mb-2 pb-1.5 border-b-2 border-slate-300">
                                <div class="text-[10px] text-slate-700 font-bold">
                                  User ID: {{ chainId }}
                                </div>
                                <div class="flex gap-1">
                                  <span v-if="hasAnyClosed(chainGroup.statuses)" 
                                        class="text-[8px] px-1.5 py-0.5 rounded-full bg-red-200 text-red-900 font-bold border border-red-400"
                                        title="Contains closed entries">
                                    CLS
                                  </span>
                                  <span v-if="chainGroup.statuses.length > 0" 
                                        class="text-[8px] px-1.5 py-0.5 rounded-full bg-blue-200 text-blue-900 font-bold border border-blue-400"
                                        title="Total entries in this chain">
                                    {{ chainGroup.statuses.length }}
                                  </span>
                                </div>
                              </div>
                              <!-- <div class="text-[8px] text-slate-500 italic mb-2 text-center bg-white rounded px-2 py-1 border border-slate-200">
                                Bookmaker/Chain identifier (user_id)
                              </div> -->
                              
                              <!-- Delta Groups -->
                              <div class="space-y-1.5">
                                <div v-for="(deltaGroup, deltaIndex) in chainGroup.deltaGroups" :key="deltaIndex"
                                     :class="[
                                       'transition-all',
                                       deltaGroup.hasChange 
                                         ? (isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                             ? 'rounded-lg border-2 bg-gray-100 border-gray-300 opacity-75'
                                             : 'rounded-lg border-2 bg-white border-green-400 shadow-sm')
                                         : 'rounded-lg border-2 bg-gray-50 border-gray-300'
                                     ]">
                                  
                                  <!-- Full Display when there's a change -->
                                  <div v-if="deltaGroup.hasChange" 
                                       :class="[
                                         'space-y-1.5 rounded-lg p-2 border-2 transition-all',
                                         isSuspended(deltaGroup.entries[0])
                                           ? 'bg-gray-100 border-gray-300 opacity-75'
                                           : isClosed(deltaGroup.entries[0])
                                             ? 'bg-gray-100 border-gray-300 opacity-75'
                                             : 'bg-white border-green-400 shadow-sm'
                                       ]">
                                    <!-- Timestamp Range -->
                                    <div :class="[
                                      'text-[10px] text-center font-semibold border-b pb-1 rounded px-2 py-0.5',
                                      isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                        ? 'text-gray-400 border-gray-300 bg-gray-50'
                                        : 'text-gray-700 border-gray-300 bg-white'
                                    ]">
                                      {{ formatDateTimeFull(deltaGroup.entries[0]?.created_at) }}
                                      <span v-if="deltaGroup.entries.length > 1" 
                                            :class="[
                                              'block text-[9px] font-normal mt-0.5',
                                              isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                                ? 'text-gray-400'
                                                : 'text-gray-500'
                                            ]">
                                        to {{ formatDateTimeFull(deltaGroup.entries[deltaGroup.entries.length - 1]?.created_at) }}
                                      </span>
                                    </div>
                                    
                                    <!-- Balance Line -->
                                    <div :class="[
                                      'text-center rounded-lg p-2 border',
                                      isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                        ? 'bg-gray-50 border-gray-300'
                                        : 'bg-white border-gray-200'
                                    ]">
                                      <div :class="[
                                        'text-base font-bold',
                                        isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                          ? 'text-gray-400'
                                          : 'text-slate-900'
                                      ]">
                                        {{ deltaGroup.entries[0]?.balance_line?.toFixed(1) || '0.0' }}
                                      </div>
                                    </div>
                                    
                                    <!-- Status Badges -->
                                    <div class="flex gap-1 justify-center">
                                      <!-- Suspended Status Badge (only when suspended, not closed) -->
                                      <div v-if="isSuspended(deltaGroup.entries[0]) && !isClosed(deltaGroup.entries[0])" 
                                           class="text-[9px] px-2 py-1 rounded-full bg-red-500 text-gray-200 font-bold border border-gray-400">
                                        SUSPENDED
                                      </div>
                                      
                                      <!-- Closed Status Badge (always show when closed) -->
                                      <div v-if="isClosed(deltaGroup.entries[0])" 
                                           class="text-[9px] px-2 py-1 rounded-full bg-red-300 text-red-800 font-bold border-2 border-red-500 shadow-sm">
                                        <i class="fas fa-lock mr-1"></i>
                                        CLOSED
                                      </div>
                                    </div>
                                    
                                    <!-- Over/Under Odds -->
                                    <div class="space-y-1">
                                      <!-- Over Section -->
                                      <div :class="[
                                        'rounded-lg px-2 py-1.5 flex items-center justify-between text-[9px] border-2',
                                        isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                          ? 'bg-gray-200 border-gray-400'
                                          : 'bg-green-100 border-green-400'
                                      ]">
                                        <span :class="[
                                          'font-bold',
                                          isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                            ? 'text-gray-500'
                                            : 'text-green-800'
                                        ]">O</span>
                                        <div class="flex items-center gap-2">
                                          <span :class="[
                                            'font-bold text-[10px]',
                                            isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                              ? 'text-gray-500'
                                              : 'text-slate-900'
                                          ]">{{ deltaGroup.entries[0]?.balance_line_over_odds?.toFixed(2) || '0.00' }}</span>
                                          <span :class="[
                                            'font-medium',
                                            isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                              ? 'text-gray-400'
                                              : 'text-slate-600'
                                          ]">{{ formatPercent(deltaGroup.entries[0]?.balance_line_over_probability) }}</span>
                                        </div>
                                      </div>
                                      
                                      <!-- Under Section -->
                                      <div :class="[
                                        'rounded-lg px-2 py-1.5 flex items-center justify-between text-[9px] border-2',
                                        isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                          ? 'bg-gray-200 border-gray-400'
                                          : 'bg-red-100 border-red-400'
                                      ]">
                                        <span :class="[
                                          'font-bold',
                                          isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                            ? 'text-gray-500'
                                            : 'text-red-800'
                                        ]">U</span>
                                        <div class="flex items-center gap-2">
                                          <span :class="[
                                            'font-bold text-[10px]',
                                            isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                              ? 'text-gray-500'
                                              : 'text-slate-900'
                                          ]">{{ deltaGroup.entries[0]?.balance_line_under_odds?.toFixed(2) || '0.00' }}</span>
                                          <span :class="[
                                            'font-medium',
                                            isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                              ? 'text-gray-400'
                                              : 'text-slate-600'
                                          ]">{{ formatPercent(deltaGroup.entries[0]?.balance_line_under_probability) }}</span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <!-- Delta Indicator -->
                                    <!-- <div v-if="deltaGroup.changes.length > 0 && deltaGroup.changes[0] !== 'initial'" 
                                         :class="[
                                           'text-[8px] font-medium text-center pt-1 border-t-2 rounded px-2 py-1',
                                           isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                             ? 'text-gray-500 border-gray-400 bg-gray-50'
                                             : 'text-blue-700 border-blue-300 bg-blue-50'
                                         ]">
                                      <span class="font-semibold">Changed:</span>
                                      <span class="ml-1">{{ deltaGroup.changes.filter(c => c !== 'initial').join(', ') }}</span>
                                    </div> -->
                                  </div>
                                  
                                  <!-- Delta Display when no change (grouped) -->
                                  <div v-else 
                                       :class="[
                                         'text-center space-y-1 rounded-lg p-2 border-2 transition-all',
                                         isSuspended(deltaGroup.entries[0])
                                           ? 'bg-gray-100 border-gray-300 opacity-75'
                                           : isClosed(deltaGroup.entries[0])
                                             ? 'bg-gray-100 border-gray-300 opacity-75'
                                             : 'bg-gray-50 border-gray-300'
                                       ]">
                                    <div :class="[
                                      'text-[9px] font-medium rounded px-2 py-1 border',
                                      isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                        ? 'text-gray-400 bg-gray-50 border-gray-300'
                                        : 'text-gray-600 bg-gray-100 border-gray-300'
                                    ]">
                                      {{ formatDateTimeFull(deltaGroup.entries[0]?.created_at) }}
                                      <span v-if="deltaGroup.entries.length > 1" 
                                            :class="[
                                              'block text-[8px] mt-0.5 font-normal',
                                              isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                                ? 'text-gray-400'
                                                : 'text-gray-500'
                                            ]">
                                        {{ deltaGroup.entries.length }} identical entries
                                      </span>
                                    </div>
                                    <div :class="[
                                      'text-base font-bold rounded-lg p-2 border',
                                      isSuspended(deltaGroup.entries[0]) || isClosed(deltaGroup.entries[0])
                                        ? 'text-gray-400 bg-gray-50 border-gray-300'
                                        : 'text-gray-700 bg-white border-gray-300'
                                    ]">
                                      {{ deltaGroup.entries[0]?.balance_line?.toFixed(1) || '0.0' }}
                                    </div>
                                    <!-- Status Badge for Delta Groups -->
                                    <div class="flex gap-1 justify-center">
                                      <div v-if="isClosed(deltaGroup.entries[0])" 
                                           class="text-[8px] px-2 py-1 rounded-full bg-red-300 text-red-800 font-bold border-2 border-red-500 shadow-sm">
                                        <i class="fas fa-lock mr-1"></i>
                                        CLOSED
                                      </div>
                                      <div v-else-if="isSuspended(deltaGroup.entries[0])" 
                                           class="text-[8px] px-2 py-1 rounded-full bg-gray-300 text-gray-600 font-bold border border-gray-400">
                                        SUSPENDED
                                      </div>
                                      <div v-else 
                                           class="text-[8px] text-green-700 font-semibold bg-green-50 rounded px-2 py-1 border border-green-300">
                                        ✓ No changes
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <!-- No Data State -->
                          <div v-else class="text-center text-slate-400 flex-1 flex flex-col justify-center items-center p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                            <i class="fas fa-inbox text-2xl text-gray-400 mb-2"></i>
                            <div class="text-lg font-bold text-gray-500 mb-1">0.0</div>
                            <div class="text-xs font-medium text-gray-400">No Data Available</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  fixtureId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['close'])

// Reactive data
const loading = ref(false)
const error = ref(null)
const timelineData = ref([])
const summary = ref({})
const playersList = ref({})
const fixtureData = ref({})

// Filters
const filters = ref({
  marketType: '',
  playerId: '',
  startDate: '',
  endDate: '',
  isBalanced: '1'  // Always fetch only balanced lines (is_balanced = 1)
})

// Function to check if entries can be grouped (all critical fields must be identical)
// Only group if is_suspended, odds (over/under), probabilities (over/under), and is_closed are all the same
const canGroupEntries = (entry1, entry2) => {
  if (!entry1 || !entry2) return false
  
  // Normalize values for comparison
  const normalize = (val) => {
    if (val === null || val === undefined) return null
    if (typeof val === 'boolean') return val ? 1 : 0
    if (typeof val === 'string' && (val === '1' || val === '0' || val === 'true' || val === 'false')) {
      return val === '1' || val === 'true' ? 1 : 0
    }
    return val
  }
  
  // Compare critical fields that determine if entries can be grouped
  const suspend1 = normalize(entry1.is_suspended)
  const suspend2 = normalize(entry2.is_suspended)
  
  const closed1 = normalize(entry1.is_closed)
  const closed2 = normalize(entry2.is_closed)
  
  const overOdds1 = normalize(entry1.balance_line_over_odds)
  const overOdds2 = normalize(entry2.balance_line_over_odds)
  
  const underOdds1 = normalize(entry1.balance_line_under_odds)
  const underOdds2 = normalize(entry2.balance_line_under_odds)
  
  const overProb1 = normalize(entry1.balance_line_over_probability)
  const overProb2 = normalize(entry2.balance_line_over_probability)
  
  const underProb1 = normalize(entry1.balance_line_under_probability)
  const underProb2 = normalize(entry2.balance_line_under_probability)
  
  // All critical fields must be identical to group
  return suspend1 === suspend2 &&
         closed1 === closed2 &&
         overOdds1 === overOdds2 &&
         underOdds1 === underOdds2 &&
         overProb1 === overProb2 &&
         underProb1 === underProb2
}

// Function to detect all changes between two entries (for display when change occurs)
const detectChanges = (entry1, entry2) => {
  if (!entry1 || !entry2) return []
  
  const changes = []
  
  // Normalize values for comparison
  const normalize = (val) => {
    if (val === null || val === undefined) return null
    if (typeof val === 'boolean') return val ? 1 : 0
    if (typeof val === 'string' && (val === '1' || val === '0' || val === 'true' || val === 'false')) {
      return val === '1' || val === 'true' ? 1 : 0
    }
    return val
  }
  
  const compare = (val1, val2) => normalize(val1) !== normalize(val2)
  
  if (compare(entry1.balance_line, entry2.balance_line)) changes.push('line')
  if (compare(entry1.balance_line_over_odds, entry2.balance_line_over_odds)) changes.push('over_odds')
  if (compare(entry1.balance_line_under_odds, entry2.balance_line_under_odds)) changes.push('under_odds')
  if (compare(entry1.balance_line_over_probability, entry2.balance_line_over_probability)) changes.push('over_prob')
  if (compare(entry1.balance_line_under_probability, entry2.balance_line_under_probability)) changes.push('under_prob')
  if (compare(entry1.is_suspended, entry2.is_suspended)) changes.push('suspended')
  if (compare(entry1.is_closed, entry2.is_closed)) changes.push('closed')
  
  return changes
}

// Function to check if entry is suspended (only check is_suspended field)
const isSuspended = (entry) => {
  if (!entry) return false
  return entry.is_suspended === 1 || entry.is_suspended === true || entry.is_suspended === '1'
}

// Function to check if entry is closed
const isClosed = (entry) => {
  if (!entry) return false
  return entry.is_closed === 1 || entry.is_closed === true || entry.is_closed === '1'
}

// Function to check if chain has any suspensions (only is_suspended)
const hasAnySuspension = (entries) => {
  if (!entries || !Array.isArray(entries)) return false
  return entries.some(entry => isSuspended(entry))
}

// Function to check if chain has any closed
const hasAnyClosed = (entries) => {
  return entries.some(entry => isClosed(entry))
}

// Process data: group by player -> market -> chain (user_id) -> detect deltas
const processedData = computed(() => {
  const playerMap = new Map()
  
  // First, group by player
  timelineData.value.forEach(entry => {
    const playerId = entry.player_id
    
    if (!playerMap.has(playerId)) {
      const playerName = playersList.value[playerId] || entry.player_name
      playerMap.set(playerId, {
        player_id: playerId,
        player_name: playerName,
        player_team_name: entry.player_team_name,
        markets: {},
        chains_count: 0
      })
    }
    
    const player = playerMap.get(playerId)
    const marketType = entry.market_type
    
    // Initialize market if not exists
    if (!player.markets[marketType]) {
      player.markets[marketType] = {
        chains: {}
      }
    }
    
    // Group by chain (user_id)
    const chainId = entry.user_id || 'unknown'
    if (!player.markets[marketType].chains[chainId]) {
      player.markets[marketType].chains[chainId] = {
        chainId: chainId,
        entries: [],
        statuses: [],
        deltaGroups: []
      }
    }
    
    // Add entry to chain
    player.markets[marketType].chains[chainId].entries.push(entry)
  })
  
  // Process each chain: sort by time (oldest first) and create delta groups
  playerMap.forEach(player => {
    Object.keys(player.markets).forEach(marketType => {
      const market = player.markets[marketType]
      const uniqueChains = new Set()
      
      Object.keys(market.chains).forEach(chainId => {
        const chain = market.chains[chainId]
        uniqueChains.add(chainId)
        
        // Sort entries by created_at (oldest first for delta detection)
        chain.entries.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        
        // Create statuses array
        chain.statuses = [...chain.entries]
        
        // Create delta groups: group consecutive entries only if all critical fields are identical
        // Critical fields: is_suspended, over_odds, under_odds, over_prob, under_prob, is_closed
        chain.deltaGroups = []
        let currentDeltaGroup = null
        
        chain.entries.forEach((entry, index) => {
          if (index === 0) {
            // First entry always starts a new group and shows full details
            currentDeltaGroup = {
              entries: [entry],
              hasChange: true, // First entry always shown in full
              changes: ['initial'] // Mark as initial state
            }
            chain.deltaGroups.push(currentDeltaGroup)
          } else {
            // Compare with the previous entry to see if we can group
            const previousEntry = chain.entries[index - 1]
            const canGroup = canGroupEntries(previousEntry, entry)
            
            if (!canGroup) {
              // Critical fields differ - must show separately with full details
              const changes = detectChanges(previousEntry, entry)
              currentDeltaGroup = {
                entries: [entry],
                hasChange: true,
                changes: changes
              }
              chain.deltaGroups.push(currentDeltaGroup)
            } else {
              // All critical fields are identical - can group as delta
              // If current group is already a delta group, add to it
              if (currentDeltaGroup && !currentDeltaGroup.hasChange) {
                // Add to existing delta group (consecutive identical entries)
                currentDeltaGroup.entries.push(entry)
              } else {
                // Start a new delta group (first identical entry after a change)
                currentDeltaGroup = {
                  entries: [entry],
                  hasChange: false,
                  changes: []
                }
                chain.deltaGroups.push(currentDeltaGroup)
              }
            }
          }
        })
        
        // Reverse delta groups to show newest first
        chain.deltaGroups.reverse()
        // Reverse entries within each group to show newest first
        chain.deltaGroups.forEach(group => {
          group.entries.reverse()
        })
      })
      
      player.chains_count = Math.max(player.chains_count, uniqueChains.size)
    })
  })
  
  return Array.from(playerMap.values())
})

const loadTimelineData = async () => {
  if (!props.fixtureId) return
  
  loading.value = true
  error.value = null
  
  try {
    const params = new URLSearchParams()
    
    if (filters.value.marketType) params.append('market_type', filters.value.marketType)
    if (filters.value.playerId) params.append('player_id', filters.value.playerId)
    if (filters.value.startDate) params.append('start_date', filters.value.startDate)
    if (filters.value.endDate) params.append('end_date', filters.value.endDate)
    if (filters.value.isBalanced !== '') params.append('is_balanced', filters.value.isBalanced)
    
    const response = await axios.get(`/api/timeline-clickhouse/${props.fixtureId}?${params.toString()}`)

    if (response.data.success) {
      timelineData.value = response.data.data || []
      summary.value = response.data.meta || {}
      playersList.value = response.data.meta?.players_list || {}
      fixtureData.value = response.data.meta?.fixture_data || {}
    } else {
      throw new Error(response.data.message || 'Failed to load timeline data')
    }
  } catch (err) {
    console.error('Error loading timeline data:', err)
    error.value = err.response?.data?.message || err.message || 'Failed to load timeline data'
  } finally {
    loading.value = false
  }
}

const getMarketDisplayName = (market) => {
  const marketNames = {
    'points': 'Points',
    'total_rebounds': 'Rebounds',
    'assists': 'Assists',
    'three_point_field_goal': '3 Points',
    'turnovers': 'Turnovers',
    'blocks': 'Blocks',
    'steals': 'Steals',
    'pra': 'PRA',
    'pr': 'PR',
    'pa': 'PA',
    'ra': 'RA',
    'bs': 'BS'
  }
  return marketNames[market] || market.replace('_', ' ')
}

const formatDateTimeFull = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  if (isNaN(date.getTime())) return timestamp
  
  // Format: DD.MM.YYYY HH:i:s (24-hour format)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  
  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`
}

const formatPercent = (value) => {
  if (value === null || value === undefined) return '0.0%'
  return (value * 100).toFixed(1) + '%'
}

// Watchers
watch(() => props.show, (newValue) => {
  if (newValue && props.fixtureId) {
    loadTimelineData()
  }
})

watch(() => props.fixtureId, (newValue) => {
  if (props.show && newValue) {
    loadTimelineData()
  }
})

// Lifecycle
onMounted(() => {
  if (props.show && props.fixtureId) {
    loadTimelineData()
  }
})
</script>

<style scoped>
/* Custom scrollbar */
.overflow-auto::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.overflow-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Horizontal scrollbar for table */
.overflow-x-auto::-webkit-scrollbar {
  height: 8px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Ensure table cells have consistent width */
.grid-cols-12 > div {
  min-width: 180px;
  width: 180px;
}

/* Market cell styling for better spacing */
.market-cell {
  min-width: 180px;
  width: 180px;
  padding: 12px;
}

/* Improve text spacing in market cells */
.market-cell .text-xs {
  line-height: 1.2;
  margin-bottom: 2px;
}

/* Better spacing for over/under sections */
.market-cell .flex {
  gap: 4px;
}
</style>